"use client";
/**
 * Tidsregistrering / Time registration — Svendetid marketing feature page.
 *
 * Built on the svendetid-ds design system (shared with /features and /pricing).
 * Language-parametrized so /features/time-registration (da) and
 * /en/features/time-registration (en) share one source.
 *
 * Distinct from /features/time-tracking: this page focuses on registering hours
 * — clocking in/out, timesheets, approval and how those hours flow to invoices.
 */
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, ImageSlot, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Tidsregistrering",
    h1: "Registrér timer, der ender på fakturaen.",
    sub: "Stempl ind og ud direkte på jobbet, godkend timesedler og send nøjagtige fakturaer — uden dobbeltarbejde og uden timer der forsvinder.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",
    trust: "Gratis at komme i gang · Ingen binding · Dansk support",
    heroShot: "App-skærm: tidsregistrering",
    featLabel: "Sådan virker det",
    featH2: "Alt du har brug for til at registrere tid",
    featSub: "Fra det første stempel ind til den færdige faktura — timerne følger automatisk med hele vejen.",
    features: [
      { icon: "time-outline", iconBg: "var(--tint-mint)", iconColor: "var(--brand)", title: "Stempl ind og ud", body: "Start og stop tiden direkte på det enkelte job eller den enkelte opgave — ét tryk, ingen formularer." },
      { icon: "briefcase-outline", iconBg: "var(--tint-teal)", iconColor: "#0E7C7B", title: "Knyttet til jobbet", body: "Hver registrering hænger på det rigtige job, så du altid ved hvem der har arbejdet hvor og hvornår." },
      { icon: "receipt-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-strong)", title: "Timer bliver til faktura", body: "Registrerede timer føres automatisk over på omkostninger og faktura — uden at taste tal ind igen." },
      { icon: "shield-checkmark-outline", iconBg: "var(--tint-rose)", iconColor: "var(--danger)", title: "Godkend timesedler", body: "Gennemse og godkend indsendte timer fra dit team, før de lander på kundens faktura." },
      { icon: "trending-up-outline", iconBg: "var(--tint-lilac)", iconColor: "#5B4B9E", title: "Overblik i realtid", body: "Se tidsforbruget pr. job og pr. medarbejder live — så du fanger budgetoverskridelser med det samme." },
      { icon: "cloud-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Virker offline", body: "Registrér tid uden netværk på pladsen — det synkroniserer automatisk, så snart du er online igen." },
    ],
    mobileH3: "Registrér tiden, mens du arbejder",
    mobileBody: "Med mobilappen stempler holdet ind og ud direkte fra pladsen. Timerne synkroniserer med kontoret med det samme, så fakturaen er klar, når jobbet er det.",
    mobileScreen: "Mobil-skærm",
    bandH2: "Klar til at få styr på timerne?",
    bandBody: "Kom i gang gratis i dag. Vælg Pro eller Business+, når du er klar — ingen binding.",
  },
  en: {
    eyebrow: "Time registration",
    h1: "Register hours that end up on the invoice.",
    sub: "Clock in and out straight on the job, approve timesheets and send accurate invoices — without double work and without hours going missing.",
    cta: "Get started – it's free",
    seePricing: "See pricing",
    trust: "Free to get started · No commitment · Danish support",
    heroShot: "App screen: time registration",
    featLabel: "How it works",
    featH2: "Everything you need to register time",
    featSub: "From the first clock-in to the finished invoice — hours carry along automatically the whole way.",
    features: [
      { icon: "time-outline", iconBg: "var(--tint-mint)", iconColor: "var(--brand)", title: "Clock in and out", body: "Start and stop time straight on a single job or task — one tap, no forms." },
      { icon: "briefcase-outline", iconBg: "var(--tint-teal)", iconColor: "#0E7C7B", title: "Tied to the job", body: "Every entry sits on the right job, so you always know who worked where and when." },
      { icon: "receipt-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-strong)", title: "Hours become invoices", body: "Registered hours carry over to costs and the invoice automatically — no re-typing numbers." },
      { icon: "shield-checkmark-outline", iconBg: "var(--tint-rose)", iconColor: "var(--danger)", title: "Approve timesheets", body: "Review and approve submitted hours from your team before they reach the customer's invoice." },
      { icon: "trending-up-outline", iconBg: "var(--tint-lilac)", iconColor: "#5B4B9E", title: "Real-time overview", body: "See time spent per job and per employee live — so you catch budget overruns right away." },
      { icon: "cloud-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Works offline", body: "Register time without a network on site — it syncs automatically the moment you're back online." },
    ],
    mobileH3: "Register time as you work",
    mobileBody: "With the mobile app the crew clocks in and out straight from the site. Hours sync with the office instantly, so the invoice is ready when the job is.",
    mobileScreen: "Mobile screen",
    bandH2: "Ready to get your hours under control?",
    bandBody: "Get started free today. Choose Pro or Business+ when you're ready — no commitment.",
  },
} as const;

type Feature = { icon: string; iconBg: string; iconColor: string; title: string; body: string };

function PhoneFrame({ imgSrc, alt, maxImgHeight = 290 }: { imgSrc: string; alt: string; maxImgHeight?: number }) {
  return (
    <div className="ds-phone-frame" style={{ width: 188, padding: 9, background: "#0A100D", border: "1px solid #2C3B35", borderRadius: 34, boxShadow: "0 20px 44px rgba(16,33,27,0.22)" }}>
      <div className="ds-phone-frame-inner" style={{ width: 170, borderRadius: 26, overflow: "hidden", background: "#fff", display: "flex", flexDirection: "column" }}>

        {/* Android status bar */}
        <div style={{ background: "#fff", padding: "6px 12px 4px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontSize: 8, fontWeight: 700, color: "#111", fontFamily: "sans-serif", letterSpacing: 0 }}>9:41</span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* Signal bars */}
            <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
              <rect x="0" y="5" width="2" height="3" rx="0.5" fill="#111"/>
              <rect x="3" y="3" width="2" height="5" rx="0.5" fill="#111"/>
              <rect x="6" y="1" width="2" height="7" rx="0.5" fill="#111"/>
              <rect x="9" y="0" width="2" height="8" rx="0.5" fill="#111"/>
            </svg>
            {/* Wifi */}
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M5 6.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" fill="#111"/>
              <path d="M2.2 4.3a4 4 0 0 1 5.6 0" stroke="#111" strokeWidth="1" strokeLinecap="round" fill="none"/>
              <path d="M0.5 2.6a6.5 6.5 0 0 1 9 0" stroke="#111" strokeWidth="1" strokeLinecap="round" fill="none"/>
            </svg>
            {/* Battery */}
            <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
              <rect x="0.5" y="0.5" width="11" height="7" rx="1.5" stroke="#111" strokeWidth="1"/>
              <rect x="1.5" y="1.5" width="8" height="5" rx="0.8" fill="#111"/>
              <path d="M12 2.5v3" stroke="#111" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Screenshot */}
        <div style={{ flex: 1, overflow: "hidden", maxHeight: maxImgHeight }}>
          <img src={imgSrc} alt={alt} style={{ display: "block", width: "100%", height: "auto" }} />
        </div>

        {/* Android gesture nav bar */}
        <div style={{ background: "#fff", padding: "8px 0 6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <div style={{ width: 52, height: 4, borderRadius: 2, background: "#222" }} />
        </div>

      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
  padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15,
};

function FeatureCard({ icon, iconBg, iconColor, title, body }: Feature) {
  return (
    <div style={cardStyle}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={24} color={iconColor} />
      </div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{body}</p>
    </div>
  );
}

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

export default function TimeRegistrationContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}`}</style>
      <style>{`
        @media (max-width: 480px) {
          .ds-phone-showcase { padding: 24px 16px !important; gap: 10px !important; }
          .ds-phone-showcase .ds-phone-frame { width: 128px !important; padding: 6px !important; border-radius: 24px !important; }
          .ds-phone-showcase .ds-phone-frame-inner { width: 116px !important; border-radius: 18px !important; }
        }
      `}</style>

      <LandingNav lang={lang} />

      {/* ===== HERO ===== */}
      <section style={{ background: "var(--green-900)", color: "var(--text-on-hero)", position: "relative", overflow: "hidden", paddingTop: 64 }}>
        <div className="ds-2col ds-hero" style={{ maxWidth: 1180, margin: "0 auto", padding: "88px 32px 96px", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 56, alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.eyebrow}</div>
            <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 58, lineHeight: 1.04, letterSpacing: "-0.025em", margin: "18px 0 0", color: "#fff", textWrap: "balance" }}>{t.h1}</h1>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "30em", margin: "22px 0 0" }}>{t.sub}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 34 }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
              <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 22px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 24, fontSize: 14, color: "var(--text-hero-muted)" }}><Icon name="checkmark-circle" size={18} color="#7FC8A0" /> {t.trust}</div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ width: 286, padding: 12, background: "#0A100D", border: "1px solid #2C3B35", borderRadius: 46, boxShadow: "0 30px 70px rgba(0,0,0,0.45)", transform: "rotate(2deg)" }}>
              <img
                src="/time_track_mobile.png"
                alt={t.heroShot}
                style={{ display: "block", width: 262, height: "auto", borderRadius: 34 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section style={{ padding: "96px 0 24px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px" }}>
          <SectionEyebrow label={t.featLabel} />
          <div style={{ textAlign: "center", maxWidth: "34em", margin: "0 auto 48px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.featH2}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-muted)", margin: "14px 0 0" }}>{t.featSub}</p>
          </div>

          <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
            {t.features.map((f) => <FeatureCard key={f.title} {...f} />)}
          </div>

          {/* Mobile app showcase */}
          <div className="ds-2col" style={{ marginTop: 20, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 20, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", overflow: "hidden", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div style={{ padding: "48px 44px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--green-900)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="phone-portrait-outline" size={24} color="#fff" /></div>
              <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 30, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, color: "var(--green-900)" }}>{t.mobileH3}</h3>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>{t.mobileBody}</p>
              <div style={{ display: "flex", gap: 10, marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.04em", color: "var(--text-muted)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", background: "var(--sage-100)", borderRadius: 8 }}><Icon name="logo-apple" size={15} /> iOS</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", background: "var(--sage-100)", borderRadius: 8 }}><Icon name="logo-android" size={15} /> Android</span>
              </div>
            </div>
            <div className="ds-phone-showcase" style={{ background: "var(--green-50)", borderLeft: "1px solid var(--border)", padding: 40, display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 18 }}>
              <PhoneFrame imgSrc="/timeregistration2.jpg" alt={t.mobileScreen} maxImgHeight={320} />
              <div style={{ marginBottom: 34 }}>
                <PhoneFrame imgSrc="/timeregistration1.jpg" alt={t.mobileScreen} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "96px 0" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--green-900)", borderRadius: 26, padding: "64px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <img src="/svendetid-logo.png" alt="" style={{ width: 64, height: 64, margin: "0 auto 18px", display: "block", opacity: 0.96 }} />
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, letterSpacing: "-0.02em", lineHeight: 1.08, margin: "0 auto", maxWidth: "18em", color: "#fff" }}>{t.bandH2}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "32em", margin: "16px auto 0" }}>{t.bandBody}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", marginTop: 34 }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
              <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/time-registration" enHref="/en/features/time-registration" />
    </div>
  );
}
